import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

export default function TacticManagementPage() {
    const [tactics, setTactics] = useState<any[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [dimensionFilter, setDimensionFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    const fetchTactics = useCallback(async () => {
        setIsFetching(true);
        try {
            const data = await apiClient.getAllTactics(page, limit, debouncedSearchTerm, dimensionFilter, statusFilter);
            setTactics(data.items);
            setTotal(data.total);
        } catch (error) {
            console.error("Failed to fetch tactics:", error);
            toast.error("Failed to load tactics");
        } finally {
            setIsFetching(false);
            if (isInitialLoading) {
                setIsInitialLoading(false);
            }
        }
    }, [page, limit, debouncedSearchTerm, dimensionFilter, statusFilter, isInitialLoading]);

    useEffect(() => {
        fetchTactics();
    }, [fetchTactics]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm, dimensionFilter, statusFilter]);

    const handleStatusChange = async (tacticId: string, isActive: boolean) => {
        try {
            await apiClient.setTacticStatus(tacticId, isActive);
            setTactics((prevTactics) =>
                prevTactics.map((tactic) =>
                    tactic._id === tacticId ? { ...tactic, is_active: isActive } : tactic
                )
            );
            toast.success(`Tactic ${tacticId} has been ${isActive ? "activated" : "deactivated"}`);
        } catch (error) {
            console.error("Failed to update tactic status:", error);
            toast.error("Failed to update tactic status");
        }
    };

    if (isInitialLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-10 w-1/3" />
                <Skeleton className="h-8 w-1/4" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-start">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground font-cabinet">
                        All Tactics
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage and review all tactics in the tactics bank.
                    </p>
                </div>
                <Badge variant="outline">Total: {total}</Badge>
            </div>
            <div className="flex justify-between">
                <Input
                    placeholder="Search by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="max-w-sm"
                />
                <div className="flex space-x-2">
                    <Select value={dimensionFilter} onValueChange={setDimensionFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Filter by dimension" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Dimensions</SelectItem>
                            <SelectItem value="reach">Reach</SelectItem>
                            <SelectItem value="brand_fit">Brand Fit</SelectItem>
                            <SelectItem value="production_readiness">Production Readiness</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[180px]">
                            <SelectValue placeholder="Filter by status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="overflow-x-auto border rounded-lg max-h-[60vh] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-muted/50 sticky top-0">
                        <tr className="border-b">
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Tactic ID
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Dimension
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                Status
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-border">
                        {isFetching ? (
                            Array.from({ length: limit }).map((_, index) => (
                                <tr key={index}>
                                    <td className="px-4 py-4"><Skeleton className="h-5 w-20" /></td>
                                    <td className="px-4 py-4"><Skeleton className="h-5 w-48" /></td>
                                    <td className="px-4 py-4"><Skeleton className="h-5 w-32" /></td>
                                    <td className="px-4 py-4"><Skeleton className="h-5 w-24" /></td>
                                </tr>
                            ))
                        ) : tactics.map((tactic) => (
                            <tr key={tactic._id}>
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {tactic._id}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {tactic.name}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {tactic.dimension}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <Switch
                                        checked={tactic.is_active}
                                        onCheckedChange={(isChecked) => handleStatusChange(tactic._id, isChecked)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex justify-between items-center p-4">
                <div className="flex items-center space-x-2">
                    <span className="text-sm text-muted-foreground">Rows per page</span>
                    <Select
                        value={String(limit)}
                        onValueChange={(value) => {
                            setLimit(Number(value));
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className="w-[70px]">
                            <SelectValue placeholder={limit} />
                        </SelectTrigger>
                        <SelectContent>
                            {[10, 20, 50].map((pageSize) => (
                                <SelectItem key={pageSize} value={String(pageSize)}>
                                    {pageSize}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center space-x-4">
                    <span className="text-sm text-muted-foreground">Page {page} of {Math.ceil(total / limit)}</span>
                    <div className="flex items-center space-x-2">
                        <Button
                            variant="outline"
                            onClick={() => setPage(page - 1)}
                            disabled={page === 1}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setPage(page + 1)}
                            disabled={page * limit >= total}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
